import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Lock } from "lucide-react";
import { HomeLayout } from "../components/layout/HomeLayout";
import { HOME_THEME as C } from "../lib/homeTheme";
// @ts-ignore — AuthContext es JSX
import { useAuth } from "@/contexts/AuthContext";
import {
  BEGINNER_PATH_CHAPTERS,
  BEGINNER_PATH_PHASES,
  isBeginnerChapterUnlocked,
  isBeginnerPathPreviewUser,
  isBeginnerPhaseUnlocked,
  loadBeginnerPathProgress,
  markBeginnerPhaseComplete,
  saveBeginnerPathProgress,
  type BeginnerPathChapter,
  type BeginnerPathPhase,
  type BeginnerPathProgress,
} from "../lib/beginnerPath/beginnerPathPlan";
import { beginnerPathUrl } from "../lib/beginnerPath/beginnerPathNav";
import { BEGINNER_PATH_PAGE_CLASS } from "../lib/beginnerPath/beginnerPathLayout";

const ACCENT: Record<BeginnerPathPhase["accent"], { border: string; badge: string; soft: string }> = {
  mint: {
    border: "rgba(153, 246, 228, 0.35)",
    badge: "rgba(153, 246, 228, 0.12)",
    soft: "#99f6e4",
  },
  lavender: {
    border: "rgba(196, 181, 253, 0.35)",
    badge: "rgba(196, 181, 253, 0.12)",
    soft: "#c4b5fd",
  },
};

function PhaseCardGrid({
  chapter,
  progress,
  preview,
  onToggle,
  onStart,
}: {
  chapter: BeginnerPathChapter;
  progress: BeginnerPathProgress;
  preview: boolean;
  onToggle: (phaseId: BeginnerPathPhase["id"]) => void;
  onStart: (phase: BeginnerPathPhase) => void;
}) {
  const unlockOpts = preview ? { preview: true } : undefined;
  const chapterOpen = isBeginnerChapterUnlocked(chapter, progress, unlockOpts);

  return (
    <ol className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 list-none p-0 m-0">
      {chapter.phases.map((phase) => {
        const done = progress.completedPhaseIds.includes(phase.id);
        const unlocked = isBeginnerPhaseUnlocked(phase.id, progress, unlockOpts);
        const locked = !unlocked;
        const accent = ACCENT[phase.accent];
        // Preview: permite CTA aunque el módulo aún sea esqueleto (available: false).
        const canNavigate =
          unlocked && Boolean(phase.path) && (phase.available !== false || preview);
        const ctaLabel =
          !preview && phase.available === false
            ? "Próximamente"
            : done
              ? "Repetir"
              : preview && phase.available === false
                ? "Abrir (preview)"
                : "Empezar";

        return (
          <li
            key={phase.id}
            className="flex flex-col rounded-2xl border p-4 sm:p-5 min-h-[11.5rem]"
            style={{
              borderColor: locked
                ? "rgba(255,255,255,0.06)"
                : done
                  ? accent.border
                  : "rgba(255,255,255,0.1)",
              background: locked
                ? "rgba(255,255,255,0.015)"
                : done
                  ? accent.badge
                  : "rgba(255,255,255,0.03)",
              opacity: locked ? 0.72 : 1,
            }}
          >
            <div className="flex items-start gap-3">
              <button
                type="button"
                onClick={() => onToggle(phase.id)}
                disabled={locked}
                className="shrink-0 flex h-9 w-9 items-center justify-center rounded-full border transition-colors disabled:cursor-not-allowed"
                style={{
                  borderColor: locked
                    ? "rgba(255,255,255,0.12)"
                    : done
                      ? accent.border
                      : "rgba(255,255,255,0.22)",
                  background: locked
                    ? "rgba(255,255,255,0.03)"
                    : done
                      ? accent.badge
                      : "rgba(255,255,255,0.04)",
                  color: locked ? "rgba(255,255,255,0.35)" : done ? accent.soft : C.text,
                }}
                aria-label={
                  locked
                    ? `Fase ${phase.order} bloqueada`
                    : done
                      ? "Marcar como pendiente"
                      : "Marcar como completada"
                }
              >
                {locked ? (
                  <Lock className="h-3.5 w-3.5" />
                ) : done ? (
                  <Check className="h-3.5 w-3.5" />
                ) : (
                  <span className="text-[11px] font-sans font-semibold tabular-nums">
                    {phase.order}
                  </span>
                )}
              </button>

              <div className="min-w-0 flex-1">
                <p className="text-[10px] uppercase tracking-[0.14em] text-white/40">
                  Paso {phase.order} · ~{phase.timeEstimateMin} min
                  {locked ? " · Bloqueado" : done ? " · Hecho" : ""}
                </p>
                <h3
                  className="mt-1 text-lg font-serif leading-snug"
                  style={{
                    color: locked ? "rgba(255,255,255,0.55)" : "rgba(255,255,255,0.92)",
                  }}
                >
                  {phase.title}
                </h3>
                <p className="text-xs font-medium text-white/50 mt-0.5">{phase.subtitle}</p>
              </div>
            </div>

            <p className="mt-3 text-sm leading-relaxed text-white/45 line-clamp-3 flex-1">
              {phase.description}
            </p>

            <div className="mt-4">
              {locked ? (
                <p className="inline-flex items-center gap-1.5 text-[11px] text-white/40">
                  <Lock className="h-3 w-3 shrink-0" />
                  {!chapterOpen
                    ? "Completa Primera canción (Camino 1)"
                    : `Completa el paso ${phase.order - 1}`}
                </p>
              ) : canNavigate ? (
                <button
                  type="button"
                  onClick={() => onStart(phase)}
                  className="inline-flex items-center gap-2 rounded-xl px-3.5 py-2 text-xs font-semibold text-[#100f0e] transition-opacity hover:opacity-90"
                  style={{
                    background:
                      phase.accent === "mint"
                        ? "linear-gradient(135deg, #99f6e4, #7ecfc0)"
                        : "linear-gradient(135deg, #c4b5fd, #a89eb8)",
                  }}
                >
                  {ctaLabel}
                  <ArrowRight className="h-3.5 w-3.5" />
                </button>
              ) : (
                <p className="text-[10px] uppercase tracking-[0.12em] text-white/35">{ctaLabel}</p>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

export const BeginnerPathPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth() as { user?: { email?: string | null } | null };
  const preview = useMemo(
    () => isBeginnerPathPreviewUser(user?.email),
    [user?.email]
  );
  const unlockOpts = preview ? { preview: true } : undefined;
  const [progress, setProgress] = useState<BeginnerPathProgress>(() =>
    loadBeginnerPathProgress()
  );

  useEffect(() => {
    saveBeginnerPathProgress(progress);
  }, [progress]);

  const completedCount = progress.completedPhaseIds.filter((id) =>
    BEGINNER_PATH_PHASES.some((p) => p.id === id)
  ).length;
  const total = BEGINNER_PATH_PHASES.length;
  const pct = total > 0 ? Math.round((completedCount / total) * 100) : 0;

  const toggleComplete = (phaseId: BeginnerPathPhase["id"]) => {
    if (!isBeginnerPhaseUnlocked(phaseId, progress, unlockOpts)) return;
    setProgress((p) => {
      if (p.completedPhaseIds.includes(phaseId)) {
        return {
          completedPhaseIds: p.completedPhaseIds.filter((id) => id !== phaseId),
        };
      }
      return markBeginnerPhaseComplete(p, phaseId);
    });
  };

  const startPhase = (phase: BeginnerPathPhase) => {
    if (phase.available === false && !preview) return;
    navigate(beginnerPathUrl(phase.path, phase.id));
  };

  return (
    <HomeLayout>
      <div className={BEGINNER_PATH_PAGE_CLASS}>
        <button
          type="button"
          onClick={() => navigate("/learn")}
          className="inline-flex items-center gap-2 text-[11px] tracking-[0.14em] uppercase transition-colors duration-300 hover:text-[#99f6e4]"
          style={{ color: C.muted }}
        >
          <ArrowLeft className="h-4 w-4" />
          Aprender
        </button>

        <header className="mt-10 mb-10 lg:mb-12">
          <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
            <div className="min-w-0 flex-1 max-w-3xl">
              <p
                className="text-xs tracking-widest uppercase"
                style={{ color: "rgba(153, 246, 228, 0.55)" }}
              >
                Rutas principiantes
              </p>
              <h1
                className="mt-4 font-serif font-light tracking-tight text-[clamp(2.2rem,5vw,3.75rem)] leading-[0.95]"
                style={{ color: C.text }}
              >
                Tu mapa de caminos
              </h1>
              <p className="mt-4 text-sm leading-relaxed text-white/60 lg:text-base max-w-2xl">
                Capítulos ordenados. Termina el Camino 1 para abrir el Camino 2 (ritmo y acordes).
              </p>
              {preview && (
                <p className="mt-2 text-[11px] uppercase tracking-[0.12em] text-[#99f6e4]/70">
                  Preview: bloqueos desactivados para tu cuenta
                </p>
              )}
            </div>

            <div className="w-full lg:w-[min(100%,360px)] shrink-0">
              <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.12em] text-white/50 mb-2">
                <span>
                  {completedCount} de {total} fases
                </span>
                <span>{pct}%</span>
              </div>
              <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${pct}%`,
                    background: "linear-gradient(90deg, #99f6e4, #c4b5fd)",
                  }}
                />
              </div>
            </div>
          </div>
        </header>

        {/* Capítulos lado a lado en desktop */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-10 xl:gap-8 2xl:gap-10">
          {BEGINNER_PATH_CHAPTERS.map((chapter) => {
            const chapterOpen = isBeginnerChapterUnlocked(chapter, progress, unlockOpts);
            const chapterDone = chapter.phases.every((p) =>
              progress.completedPhaseIds.includes(p.id)
            );
            const chapterCompleted = chapter.phases.filter((p) =>
              progress.completedPhaseIds.includes(p.id)
            ).length;

            return (
              <section
                key={chapter.id}
                aria-labelledby={`chapter-${chapter.id}`}
                className="min-w-0 rounded-3xl border p-5 sm:p-6 lg:p-7"
                style={{
                  borderColor: chapterOpen ? "rgba(255,255,255,0.1)" : "rgba(255,255,255,0.06)",
                  background: "rgba(255,255,255,0.02)",
                }}
              >
                <div className="mb-5">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <p
                      className="text-[11px] tracking-[0.16em] uppercase"
                      style={{
                        color:
                          chapter.order === 1
                            ? "rgba(153,246,228,0.55)"
                            : "rgba(196,181,253,0.55)",
                      }}
                    >
                      Capítulo {chapter.order}
                    </p>
                    {!chapterOpen && (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] text-white/40">
                        <Lock className="h-3 w-3" /> Bloqueado
                      </span>
                    )}
                    {chapterDone && (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] text-[#99f6e4]/80">
                        <Check className="h-3 w-3" /> Completado
                      </span>
                    )}
                  </div>
                  <h2
                    id={`chapter-${chapter.id}`}
                    className="font-serif font-light text-[clamp(1.35rem,2.5vw,1.85rem)] tracking-tight"
                    style={{ color: chapterOpen ? C.text : "rgba(255,255,255,0.45)" }}
                  >
                    {chapter.title}
                  </h2>
                  <p className="mt-1 text-sm font-medium text-white/55">{chapter.subtitle}</p>
                  <p className="mt-2 text-sm leading-relaxed text-white/45">{chapter.description}</p>
                  <p className="mt-3 text-[10px] uppercase tracking-[0.12em] text-white/35">
                    {chapterCompleted}/{chapter.phases.length} fases
                  </p>
                </div>

                <PhaseCardGrid
                  chapter={chapter}
                  progress={progress}
                  preview={preview}
                  onToggle={toggleComplete}
                  onStart={startPhase}
                />
              </section>
            );
          })}
        </div>
      </div>
    </HomeLayout>
  );
};

export default BeginnerPathPage;
